"use client";

/**
 * Icon Registry
 * - Maps icon names (strings from Studio configs) to React icon components.
 * - Resolves components from icon libraries (lucide-react, @untitledui/icons).
 *
 * Why "use client":
 * - Icon components are client-only React components (they render SVGs).
 *
 * Loading model:
 * - Both icon libraries are imported STATICALLY at module scope and resolved
 *   synchronously. The earlier async `import()` approach made the server
 *   render real library icons (the promise resolved before SSR emitted
 *   markup) while the client's first synchronous render fell back to the
 *   hand-drawn icons — a guaranteed hydration mismatch. Static imports give
 *   the exact same resolution on both the server and the client.
 *
 * Resolution strategy:
 * - Lucide is the primary icon collection and is searched first.
 * - For known names we try the curated candidates in icon-candidates.ts, each
 *   expanded with numeric suffixes to cover @untitledui/icons exports.
 * - Unknown names fall back to a PascalCase heuristic.
 * - The hand-drawn fallback is the last resort when nothing matches.
 */

import { createElement } from "react";
import * as lucideIcons from "lucide-react";
import * as untitledIcons from "@untitledui/icons";
import { ICON_CANDIDATES, type IconName } from "./icon-candidates";

export type { IconName } from "./icon-candidates";

export interface IconProps {
  size?: number | string;
  strokeWidth?: number;
  className?: string;
  color?: string;
  title?: string;
  style?: unknown;
  [key: string]: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ResolvedIcon = any;

type FallbackPath = {
  d: string;
  fill?: string;
  strokeLinecap?: "round" | "square" | "butt";
  strokeLinejoin?: "round" | "miter" | "bevel";
};

function toCssSize(value: number | string | undefined): number | string {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const raw = String(value ?? "").trim();
  return raw || 16;
}

function makeFallbackIcon(paths: readonly FallbackPath[]) {
  return function FallbackIcon({
    size = 16,
    strokeWidth = 2,
    className,
    color,
    title,
    style,
    ...rest
  }: IconProps) {
    const resolvedSize = toCssSize(size);

    return createElement(
      "svg",
      {
        ...rest,
        width: resolvedSize,
        height: resolvedSize,
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: color ?? "currentColor",
        strokeWidth,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        focusable: false,
        "aria-hidden": true,
        className,
        style: style as object | undefined,
      },
      title ? createElement("title", null, title) : null,
      ...paths.map((path, index) =>
        createElement("path", {
          key: index,
          d: path.d,
          fill: path.fill,
          strokeLinecap: path.strokeLinecap,
          strokeLinejoin: path.strokeLinejoin,
        }),
      ),
    );
  };
}

function makeSettingsFallbackIcon() {
  return function SettingsFallbackIcon({
    size = 16,
    strokeWidth = 2,
    className,
    color,
    title,
    style,
    ...rest
  }: IconProps) {
    const resolvedSize = toCssSize(size);

    return createElement(
      "svg",
      {
        ...rest,
        width: resolvedSize,
        height: resolvedSize,
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: color ?? "currentColor",
        strokeWidth,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        focusable: false,
        "aria-hidden": true,
        className,
        style: style as object | undefined,
      },
      title ? createElement("title", null, title) : null,
      createElement("circle", { cx: 12, cy: 12, r: 3.5 }),
      createElement("path", {
        d: "M12 2.75v2.25M12 19v2.25M4.75 12H7M17 12h2.25M5.64 5.64l1.59 1.59M16.77 16.77l1.59 1.59M18.36 5.64l-1.59 1.59M6.23 16.77l-1.59 1.59",
      }),
    );
  };
}

const FALLBACK_ICON_ALIASES: Record<string, string> = {
  back: "arrow-left",
  cog: "settings",
  gear: "settings",
  settings: "settings",
  plane: "send",
  "paper-plane": "send",
  paperplane: "send",
};

const FALLBACK_ICON_COMPONENTS: Record<string, ResolvedIcon> = {
  "arrow-left": makeFallbackIcon([{ d: "M15 6L9 12L15 18" }, { d: "M9 12H21" }]),
  "arrow-right": makeFallbackIcon([{ d: "M9 6L15 12L9 18" }, { d: "M3 12H15" }]),
  "chevron-left": makeFallbackIcon([{ d: "M15 6L9 12L15 18" }]),
  "chevron-right": makeFallbackIcon([{ d: "M9 6L12 12L9 18" }]),
  "chevron-up": makeFallbackIcon([{ d: "M6 15L12 9L18 15" }]),
  "chevron-down": makeFallbackIcon([{ d: "M6 9L12 15L18 9" }]),
  close: makeFallbackIcon([{ d: "M6 6L18 18" }, { d: "M18 6L6 18" }]),
  x: makeFallbackIcon([{ d: "M6 6L18 18" }, { d: "M18 6L6 18" }]),
  menu: makeFallbackIcon([{ d: "M4 7H20" }, { d: "M4 12H20" }, { d: "M4 17H20" }]),
  list: makeFallbackIcon([{ d: "M4 7H20" }, { d: "M4 12H20" }, { d: "M4 17H20" }]),
  user: makeFallbackIcon([{ d: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" }, { d: "M20 21a8 8 0 0 0-16 0" }]),
  heart: makeFallbackIcon([{ d: "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z" }]),
  "message-square": makeFallbackIcon([{ d: "M21 15a2 2 0 0 1-2 2H8l-5 5V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }]),
  "check-square": makeFallbackIcon([{ d: "M5 5h14v14H5z" }, { d: "M9 12l2 2 4-4" }]),
  plus: makeFallbackIcon([{ d: "M12 5v14" }, { d: "M5 12h14" }]),
  trash: makeFallbackIcon([{ d: "M3 6h18" }, { d: "M8 6V4h8v2" }, { d: "M6 6l1 12h10l1-12" }, { d: "M10 11v6" }, { d: "M14 11v6" }]),
  "log-out": makeFallbackIcon([{ d: "M10 17l5-5-5-5v3H3v4h7v3z" }, { d: "M21 5v14" }]),
  send: makeFallbackIcon([{ d: "M22 2L11 13" }, { d: "M22 2L15 22L11 13L2 9L22 2Z" }]),
  settings: makeSettingsFallbackIcon(),
};

/**
 * Library modules in preference order. Lucide is searched first so
 * Lucide-compatible export names win for the navigation icons.
 */
const iconModules: readonly Record<string, unknown>[] = [
  lucideIcons as unknown as Record<string, unknown>,
  untitledIcons as unknown as Record<string, unknown>,
];

function toPascalCaseFromKebab(input: string): string {
  return String(input ?? "")
    .trim()
    .split(/[-_\s]+/g)
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join("");
}

function isProbablyReactComponent(value: unknown): value is ResolvedIcon {
  if (!value) return false;
  const type = typeof value;
  if (type === "function") return true;
  return type === "object" && "$$typeof" in (value as Record<string, unknown>);
}

function resolveFallbackIcon(normalizedName: string): ResolvedIcon | null {
  const aliased = FALLBACK_ICON_ALIASES[normalizedName] ?? normalizedName;
  return FALLBACK_ICON_COMPONENTS[aliased] ?? null;
}

function resolveFromCandidates(candidates: readonly string[]): ResolvedIcon | null {
  for (const mod of iconModules) {
    for (const key of candidates) {
      const candidate = mod[key];
      if (isProbablyReactComponent(candidate)) return candidate;
    }
  }
  return null;
}

function withNumberedVariants(candidates: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    for (const variant of [candidate, `${candidate}01`, `${candidate}02`, `${candidate}03`]) {
      if (!seen.has(variant)) {
        seen.add(variant);
        out.push(variant);
      }
    }
  }
  return out;
}

function resolveByHeuristic(normalizedName: string): ResolvedIcon | null {
  const base = toPascalCaseFromKebab(normalizedName);
  if (!base) return null;
  return resolveFromCandidates([base, `${base}01`, `${base}02`, `${base}03`, `${base}Circle`, `${base}Square`]);
}

const resolvedCache = new Map<string, ResolvedIcon | null>();
const MAX_CACHE_SIZE = 500;

function getCachedOrResolve(normalizedName: string): ResolvedIcon | null {
  const existing = resolvedCache.get(normalizedName);
  if (existing !== undefined) return existing;
  if (resolvedCache.size > MAX_CACHE_SIZE) resolvedCache.clear();

  const candidates = (ICON_CANDIDATES as Record<string, readonly string[]>)[normalizedName] ?? null;
  let resolved = candidates
    ? resolveFromCandidates(withNumberedVariants(candidates))
    : resolveByHeuristic(normalizedName);
  if (!resolved) resolved = resolveFallbackIcon(normalizedName);

  resolvedCache.set(normalizedName, resolved);
  return resolved;
}

export const IconRegistry = {
  /**
   * Compatibility no-op. Icons are resolved synchronously via static
   * imports, so there is no async initialization to await. Kept so existing
   * callers (e.g. `void IconRegistry.init()` on mount) keep working.
   */
  init: (): Promise<void> => Promise.resolve(),

  get: (name: string | undefined): ResolvedIcon | null => {
    if (!name) return null;
    const normalized = String(name).trim().toLowerCase();
    if (!normalized) return null;
    return getCachedOrResolve(normalized);
  },

  has: (name: string | undefined): boolean => {
    if (!name) return false;
    const normalized = String(name).trim().toLowerCase();
    if (!normalized) return false;
    return Boolean(getCachedOrResolve(normalized));
  },

  isReady: (): boolean => true,

  list: (): IconName[] => Object.keys(ICON_CANDIDATES) as IconName[],
};